import { useEffect, useMemo, useRef, useState } from 'react';

import { AwardVariant, CatalogueItem, ProductAxis, ProductGroupVariant } from '~/utils/types';
import {
  AxisSelection,
  ValueState,
  commonValue,
  pendingAxis,
  pointsRangeFor,
  resolveVariant,
  selectValue,
  selectionFromAwardId,
  valueState,
  variantLineItem,
  visibleAxes,
} from '~/utils/productGroup';

type Options = {
  /** Premio a preseleccionar (el de la URL). Sin él, el producto entra sin elegir. */
  initialAwardId?: number | null;
  /** Se llama al cambiar la variante elegida (no en la carga inicial). */
  onVariantChange?: (variant: ProductGroupVariant | undefined) => void;
};

export type VariantSelection = ReturnType<typeof useVariantSelection>;

/**
 * Estado de compra de un producto en sus tres modos, según lo que mande el back:
 * 1. Producto con variantes (`group`): solo los ejes → variante (premio)
 *    elegida. El back solo manda `group` si ninguna variante tiene DbVariant
 *    activas, así que aquí no hay segundo selector.
 * 2. DbVariant (sin `group`, con `award.variants`): los chips de siempre.
 * 3. Premio simple: nada que elegir.
 *
 * Quien lo usa debe montarse con `key` por producto: el estado no se arrastra
 * de un producto a otro.
 */
export function useVariantSelection(item: CatalogueItem, options: Options = {}) {
  const { initialAwardId, onVariantChange } = options;
  const group = item.group ?? null;
  const axes: ProductAxis[] = useMemo(() => (group ? visibleAxes(group) : []), [group]);

  const [selection, setSelection] = useState<AxisSelection>(() =>
    group ? selectionFromAwardId(group, axes, initialAwardId) : {},
  );
  // Premio fijado por la URL: si varias variantes comparten los ejes visibles
  // (difieren solo en un eje HIDDEN), es esa y no otra. Elegir un valor lo suelta.
  const [pinnedAwardId, setPinnedAwardId] = useState<number | null>(initialAwardId ?? null);
  const [dbVariant, setDbVariant] = useState<AwardVariant | undefined>();

  const variant = useMemo(
    () => (group ? resolveVariant(group, axes, selection, pinnedAwardId) : undefined),
    [group, axes, selection, pinnedAwardId],
  );

  const variantId = variant?.awardId;
  const onChangeRef = useRef(onVariantChange);
  onChangeRef.current = onVariantChange;
  const lastVariantId = useRef(variantId);
  useEffect(() => {
    if (lastVariantId.current === variantId) return;
    lastVariantId.current = variantId;
    onChangeRef.current?.(variant);
  }, [variantId, variant]);

  // DbVariant solo sin `group`: nunca salen los dos selectores a la vez.
  const dbVariants: AwardVariant[] = useMemo(
    () => (group ? [] : (item.award.variants ?? []).filter((v) => v.isActive)),
    [group, item.award.variants],
  );

  // Línea a agregar al carrito: el premio de la variante (o el premio simple).
  const lineItem: CatalogueItem | undefined = useMemo(() => {
    if (!group) return item;
    return variant ? variantLineItem(item, group, axes, variant) : undefined;
  }, [group, variant, item, axes]);

  const pending = group ? pendingAxis(axes, selection) : undefined;
  const needsDbVariant = dbVariants.length > 0 && !dbVariant;
  const unavailable = Boolean(variant && !variant.available);

  const points = group
    ? variant
      ? { min: variant.points, max: variant.points }
      : pointsRangeFor(group, axes, selection)
    : { min: item.points, max: item.points };

  const display = {
    name: group?.name || item.award.name,
    image: variant?.mainImage || item.award.mainImage,
    code: group ? (variant?.code ?? commonValue(group, (v) => v.code)) : item.award.code,
    model: group ? (variant?.model ?? commonValue(group, (v) => v.model)) : item.award.model,
  };

  return {
    group,
    axes,
    selection,
    variant,
    dbVariants,
    dbVariant,
    setDbVariant,
    lineItem,
    pending,
    needsDbVariant,
    unavailable,
    points,
    display,
    stateOf: (axisKey: string, valueKey: string): ValueState =>
      group ? valueState(group, axes, selection, axisKey, valueKey) : 'disabled',
    select: (axisKey: string, valueKey: string) => {
      if (!group || selection[axisKey] === valueKey) return;
      setPinnedAwardId(null);
      setSelection((prev) => selectValue(group, axes, prev, axisKey, valueKey));
    },
    /** Sincroniza con un premio externo (URL al ir atrás/adelante). */
    selectAwardId: (awardId: number) => {
      if (!group) return;
      setPinnedAwardId(awardId);
      setSelection(selectionFromAwardId(group, axes, awardId));
    },
  };
}
